import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Star } from 'lucide-react';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [userName, setUserName] = useState(''); // Added state for the user's name
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that they entered a name
    if (!userName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setSubmitting(true);
    try {
      // Send the review anonymously with the provided name
      await axios.post(
        `${API}/reviews`,
        { 
          user_name: userName, 
          rating, 
          comment 
        }
      );
      toast.success('Review submitted successfully!');
      setShowForm(false);
      setRating(5);
      setComment('');
      setUserName('');
      fetchReviews();
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="reviews-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-zinc-500">Testimonials</span>
            <h1 className="font-outfit text-4xl md:text-5xl font-black text-white mt-2">
              Student <span className="text-red-500">Reviews</span>
            </h1>
            <p className="text-zinc-400 mt-4 max-w-2xl">
              See what our students have to say about their learning experience at STUDY POINT.
            </p>
          </div>
          
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
            data-testid="write-review-btn"
          >
            {showForm ? 'Cancel Review' : 'Write a Review'}
          </Button>
        </div>

        {/* Review Form */}
        {showForm && (
          <div className="card-hover bg-[#121212] p-6 rounded-sm mb-8 animate-fadeIn">
            <h3 className="font-outfit text-xl font-bold text-white mb-4">Share Your Experience</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Your Name</label>
                <Input
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="John Doe"
                  className="bg-[#0A0A0A] border-zinc-800 focus:border-red-500 text-white placeholder:text-zinc-500"
                  required
                  data-testid="review-name"
                />
              </div>
              
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Rating</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      className="focus:outline-none"
                      data-testid={`rating-star-${star}`}
                    >
                      <Star
                        className={`w-8 h-8 transition-colors ${
                          star <= rating
                            ? 'text-yellow-500 fill-yellow-500'
                            : 'text-zinc-600 hover:text-yellow-500/50'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Your Review</label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Share your experience with STUDY POINT..."
                  className="bg-[#0A0A0A] border-zinc-800 focus:border-red-500 text-white placeholder:text-zinc-500 min-h-[120px]"
                  required
                  data-testid="review-comment"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  type="submit"
                  disabled={submitting || !comment.trim() || !userName.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-sm"
                  data-testid="submit-review-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Review'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Reviews Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-500 text-lg">No reviews yet.</p>
            <p className="text-zinc-600 mt-2">Be the first to share your experience!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, idx) => (
              <div
                key={review.review_id}
                className={`card-hover bg-[#121212] p-6 rounded-sm animate-fadeIn stagger-${(idx % 5) + 1}`}
                data-testid={`review-${review.review_id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      className={`w-4 h-4 ${
                        i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-zinc-600'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-zinc-400 mb-4">{review.comment}</p>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-white">{review.user_name}</div>
                  <div className="text-xs text-zinc-600">
                    {new Date(review.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews;
